'use client';

import { useRef, useState, type RefObject } from 'react';

import type { SubcategoryGridLimits } from './subcategoryGrid.tsx';

/**
 * The state a capped subcategory grid runs on: whether it is showing
 * everything, and what happens to the reader's position when it stops.
 *
 * A hook rather than a component so the grid stays open anatomy — a tenant maps
 * its own taxonomy into whatever parts it composes, instead of handing a shared
 * component a renderer. It is also what keeps the parts server components: the
 * only client code the section needs is this, in the tenant's own client
 * composition.
 *
 * It decides nothing about copy or appearance. Whether a control is worth
 * composing at all is answered here — that depends on the tile count against
 * the tenant's smallest cap — but what the control says, and where it sits, is
 * the caller's.
 */

export type UseSubcategoryDisclosureParameters = {
  /** How many tiles the grid holds in total. */
  readonly count: number;
  readonly limits: SubcategoryGridLimits;
};

export type SubcategoryDisclosure = {
  readonly isExpanded: boolean;
  /** Flips the grid open, or closed and back to where the reader was. */
  readonly toggle: () => void;
  /**
   * Attach to `SubcategoryGrid`. Collapsing the grid can remove screens of
   * content from ABOVE the control that was just pressed, which would otherwise
   * leave the reader somewhere further down the page than they had ever been.
   */
  readonly gridRef: RefObject<HTMLElement | null>;
  /**
   * Whether any step caps the grid. The phone limit is the smallest, so a count
   * within it is a grid nothing is hiding at any width — and a control offering
   * to reveal nothing is worse than no control.
   */
  readonly hasHiddenTiles: boolean;
};

export const useSubcategoryDisclosure = ({
  count,
  limits,
}: UseSubcategoryDisclosureParameters): SubcategoryDisclosure => {
  const [isExpanded, setIsExpanded] = useState(false);
  const gridRef = useRef<HTMLElement>(null);

  return {
    isExpanded,
    gridRef,
    hasHiddenTiles: count > limits.pho,
    toggle: () => {
      if (isExpanded) {
        gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      setIsExpanded((previous) => !previous);
    },
  };
};
